import { useCallback } from 'react';
import { type AiUsage, getAiUsage } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { Metric } from './ui.js';

/** Compact token counts: 1234 → "1.2K", 3_400_000 → "3.4M". */
function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/**
 * Internal-only AI usage monitor for an account: query count and estimated
 * model tokens (all-time + current month). Not part of the API/SDK surface —
 * fetched via the admin direct-fetch helper.
 */
export function AiUsageTile({ accountId }: { accountId: string }) {
  const { data, error } = useAsyncData(useCallback(() => getAiUsage(accountId), [accountId]));

  if (error) return <span className="text-xs text-rose-600">Couldn’t load AI usage</span>;
  if (!data) return <span className="text-xs text-neutral-400">Loading AI usage…</span>;

  const u: AiUsage = data;
  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
        AI usage (internal) · this month {u.current.period}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Queries (mo)" value={u.current.queries.toLocaleString()} tone="indigo" />
        <Metric label="Est. tokens (mo)" value={fmtNum(u.current.tokens)} tone="indigo" />
        <Metric label="Queries (all)" value={u.total.queries.toLocaleString()} tone="neutral" />
        <Metric label="Est. tokens (all)" value={fmtNum(u.total.tokens)} tone="neutral" />
      </div>
    </div>
  );
}
